import { memo } from 'react';
import {
  Box,
  Select,
  MenuItem,
  useTheme,
  TextField,
  Typography,
  FormControl,
  useMediaQuery
} from '@mui/material';
import { Lock, Edit } from '@mui/icons-material';
import { FieldRowProps } from './FieldRow.types';
import { CustomDatePicker } from '@/components';

/**
 * Field row component for displaying and editing individual station profile fields
 * Renders different input types based on field configuration with visual change indicators
 *
 * Features:
 * - Multiple input types (text, select, date) with appropriate validation
 * - Visual indicators for field changes and edit state
 * - Lock/edit icons based on field editability
 * - URL validation for photo and Google Maps fields
 * - Number input for GPS coordinates with precision
 * - Date picker with Ukrainian localization
 * - Status translation for user-friendly display
 * - Multiline text support for longer fields
 *
 * @param props - Component props containing field configuration and callbacks
 * @returns JSX element containing the complete field row with label, input, and indicators
 */
export const FieldRow = memo(({
    label,
    field,
    value,
    type = 'text',
    options = [],
    multiline = false,
    hasError = false,
    isFieldChanged,
    isFieldEditable,
    onFieldChange,
    translateStatus,
    formatDate
  }: FieldRowProps): JSX.Element => {
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  return (
      <Box className={`station-profile-field-row ${isFieldChanged(field) ? 'changed' : ''} ${hasError ? 'error' : ''}`}>
        <Box className={`station-profile-field-icon ${isFieldEditable(field) ? 'editable' : 'locked'}`}>
          {isFieldEditable(field) ? <Edit sx={{ fontSize: '16px' }} /> : <Lock sx={{ fontSize: '16px' }} />}
        </Box>

        <Typography className="station-profile-field-label">{label}:</Typography>

        {isFieldEditable(field) ? (
          type === 'select' ? (
            <FormControl size="small" className="station-profile-form-control">
              <Select value={value || ''} onChange={(e) => onFieldChange(field, e.target.value)} displayEmpty>
                {options.map((option) => (
                  <MenuItem key={option.id || option.value} value={option.value || option.name}>
                    {option.label || option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : type === 'date' ? (
            <CustomDatePicker
              value={value}
              onChange={(newValue) => onFieldChange(field, newValue)}
              className="station-profile-date-picker"
              hideClearButton={isMobile}
            />
          ) : (
            <TextField
              value={value || ''}
              onChange={(e) => onFieldChange(field, e.target.value)}
              size="small"
              multiline={multiline}
              rows={multiline ? 2 : 1}
              className={`station-profile-text-field ${multiline ? 'multiline' : ''}`}
              type={field.includes('latitude') || field.includes('longitude') ? 'number' : 'text'}
              inputProps={field.includes('latitude') || field.includes('longitude') ? { step: 0.000001 } : {}}
              error={Boolean((field === 'photo_url' || field === 'google_maps_url') && value && !/^https?:\/\/.+/.test(value))}
              helperText={(field === 'photo_url' || field === 'google_maps_url') && value && !/^https?:\/\/.+/.test(value) ? 'Невірний формат URL' : ''}
              FormHelperTextProps={{ className: 'station-profile-error-message' }}
            />
          )
        ) : (
          <Typography className="station-profile-field-value">
            {type === 'date' ? formatDate(value) :
                  field === 'status' ? translateStatus(value) :
                      (field === 'photo_url' || field === 'google_maps_url') && value ? (
              /^https?:\/\/.+/.test(value) ? (
                <a href={value} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="station-profile-url-link">
                  Посилання
                </a>
              ) : (
                <span style={{ color: '#d32f2f' }}>Невірний формат URL</span>
              )
            ) : (value !== undefined && value !== null && value !== '') ? value : '-'}
          </Typography>
        )}
      </Box>
  );
});
